// Brookline Athletic Fields Needs Assessment and Master Plan
// Source: Town of Brookline, Parks, Open Space and Recreation — 2020 Athletic
//   Fields Needs Assessment and Master Plan (November 2020; prepared by GreenPlay,
//   LLC with RRC Associates for the Parks and Open Space Division and Brookline
//   Recreation Department).
// This page presents the plan's Recommendations and Action Plan (Section V) — the
//   athletic-field-relevant themes and recommendations carried into the 2020
//   Brookline Parks, Open Space and Recreation Strategic Master Plan Update
//   (approved September 2020 by the Park and Recreation Commission). Each action
//   item's text and attributes (Timeline, Responsibility, Financial Impact,
//   Projected Capital Costs, Public Benefit, Status in 2020) are drawn near-verbatim
//   from the plan's Action Plan matrix. Because this is the athletic-fields subset
//   of a broader plan, the theme/recommendation numbering is not continuous (there
//   is no Theme 3 in this subset). The plan's Tiered Athletic Field System
//   (permitted-hours tiers, maintenance calendars, field-closure/rest practices),
//   its Decision Matrix, and the field inventory/condition assessments are in the
//   source document but not reproduced here.
// Page created August 2026.

export const athleticFieldsData = {
  planYear: '2020',
  updatedDate: 'August 2026',
  introduction: {
    overview: [
      'The 2020 Athletic Fields Needs Assessment and Master Plan sets a 20-year vision and framework for Brookline’s athletic fields — their management, maintenance, staffing, design, funding, policy, land acquisition, permitting, programs, and partnerships. It was prepared for the Town’s Parks and Open Space Division and Brookline Recreation Department (BREC) by GreenPlay, LLC with RRC Associates (November 2020).',
      'This page presents the plan’s Recommendations and Action Plan — the athletic-field-relevant themes and recommendations carried into the 2020 Brookline Parks, Open Space and Recreation Strategic Master Plan Update (approved September 2020 by the Park and Recreation Commission). Each action item lists its timeline, responsible parties, financial impact, projected capital costs, public benefit, and its status as of 2020.',
      'The plan also includes a recommended Tiered Athletic Field System (permitted-hours tiers, maintenance calendars, and field-closure/rest practices) and a Decision Matrix for evaluating natural grass versus synthetic turf sites. Those sections, along with the full field inventory and condition assessments, are in the source document but are not reproduced here.'
    ],
    itemsNote: [
      'The recommendations below are grouped by the plan’s themes and lettered recommendation areas (for example, 1.A, 1.B). They are the athletic-field-relevant subset of the broader 2020 Strategic Master Plan Update, so the numbering is not continuous — there is no Theme 3 in this subset.',
      'Each item carries a status as of 2020, indicating whether it was new, revised, or carried forward from the 2006 Master Plan.'
    ]
  },
  legend: {
    status: [
      { code: 'New (2020)', label: 'New as of the 2020 Master Plan Update' },
      { code: 'Revised (2020)', label: 'Revised in the 2020 Master Plan Update' },
      { code: 'Remaining from 2006', label: 'A goal remaining or carried from the 2006 Master Plan' }
    ]
  },
  themes: [
    {
      id: 'additional-facilities-and-public-spaces',
      number: 1,
      name: 'Brookline Needs Additional Facilities and Public Spaces',
      description: 'The Town requires additional acreage for both active and passive uses, with primary needs for multi-purpose synthetic turf fields, baseball fields, indoor recreation space, and additional parkland.',
      items: [
        {
          id: '1.A',
          category: 'Add Additional Outdoor Recreational Amenities Spaces',
          title: 'Add 73–105 acres of land allocated for a balance of uses. Identify critical needs and parcels for land acquisition. Secure funds and negotiate acquisition.',
          timeline: '0–12 years',
          timelineDetail: 'Identify parcels, secure funds, and reach settlement with landowners.',
          responsibility: 'Conservation Commission, Park and Recreation Commission and other Town Boards/Commissions/groups with the Parks and Open Space Division, in coordination with BREC, Planning, Legal and Select Board.',
          financialImpact: 'Land acquisition cost: allocate funds as specific parcels are identified. Operational costs for staff time and/or consultant costs.',
          capitalCosts: 'Cost per acre TBD.',
          publicBenefit: 'Meets Brookline’s projected land acquisition needs based on a LOS standard of 10.1–10.5 acre/1,000 residents. Achieves a major objective of the Master Plan to meet Brookline’s open space, athletic and recreation needs for both passive and active recreation and land conservation.',
          status: 'Revised (2020)'
        },
        {
          id: '1.A',
          category: 'Add Additional Outdoor Recreational Amenities Spaces',
          title: 'Create a town-wide land acquisition strategic plan (including adoption of the Community Preservation Act).',
          timeline: '3–6 years',
          timelineDetail: 'Establish land acquisition priorities, identify parcels, develop communication strategy, and identify funding.',
          responsibility: 'Park and Recreation Commission, Conservation Commission and the Division of Parks and Open Space, in coordination with Planning, Legal and Select Board.',
          financialImpact: 'Land acquisition cost: allocate funds as specific parcels are identified. Operational costs for staff time and/or consultant costs.',
          capitalCosts: '$50,000–$75,000 for consulting services.',
          publicBenefit: 'Meets Brookline’s projected land acquisition needs based on a LOS standard of 10.1–10.5 acre/1,000 residents. Achieves a major objective of the Master Plan to meet Brookline’s open space, athletic and recreation needs for both passive and active recreation and land conservation.',
          status: 'Remaining from 2006'
        },
        {
          id: '1.B',
          category: 'Multi-purpose Synthetic Turf Fields',
          title: 'Plan for additional athletic fields: 4 new multi-purpose synthetic turf fields to accommodate the largest regulation-size HS sports (lighted); 2 converted multi-purpose synthetic turf fields (lighted); 2 new full-size natural grass baseball diamonds (1 lighted); 2 men’s softball natural grass fields (lighted); and 4 natural grass little league fields.',
          timeline: '0–6 years',
          timelineDetail: 'Identify parcels, secure funds, and reach settlement with landowners.',
          responsibility: 'Conservation Commission, Park and Recreation Commission and other Town Boards/Commissions/groups with the Parks and Open Space Division, in coordination with BREC, Planning, Legal, and Select Board.',
          financialImpact: 'Land acquisition cost, plus field design and construction.',
          capitalCosts: 'Estimated construction costs (assume design, construction, parking, restrooms, etc.): 4 synthetic turf fields, lighted — $2,500,000–$3,000,000 each (land TBD); 2 converted multi-purpose synthetic turf fields — $1,250,000–$1,500,000 each (land $0); 2 full-size natural grass baseball diamonds, lighted — $750,000–$950,000 each ($950,000–$1,200,000 lighted; land TBD); 2 men’s softball natural grass fields, lighted — $950,000–$1,200,000 (land TBD); 4 natural grass little league fields — $750,000–$950,000 each (land TBD).',
          publicBenefit: 'Provides increased facilities to meet Brookline’s demand for high-quality playable fields.',
          status: 'New (2020)'
        }
      ]
    },
    {
      id: 'creative-to-achieve-goals',
      number: 2,
      name: 'Brookline Needs to be Creative to Achieve its Goals',
      description: 'With little available land and constrained budgets, meeting the Town’s field and recreation needs will require creative use of facilities, strategic partnerships, alternative funding, and appropriate staffing.',
      items: [
        {
          id: '2.D',
          category: 'Allocate Staff Resources to Creative Strategies',
          title: 'Parks and Open Space Division should add staff positions — currently, two full-time athletic field maintenance technicians; future staff needs include additional full-time technicians if athletic fields are developed (about .5 FTE per natural grass field and .25 FTE per synthetic turf field developed).',
          timeline: '0–3 years',
          timelineDetail: '',
          responsibility: 'Division of Parks and Open Space.',
          financialImpact: 'Increases the operational budget by approximately $60,000/year plus benefits per FTE (operational cost).',
          capitalCosts: 'N/A',
          publicBenefit: 'Provides improved athletic field playability and usability.',
          status: 'New (2020)'
        },
        {
          id: '2.D',
          category: 'Allocate Staff Resources to Creative Strategies',
          title: 'BREC should add staff positions — currently, a full-time Marketing Manager.',
          timeline: '0–3 years',
          timelineDetail: '',
          responsibility: 'Brookline Recreation Department (BREC).',
          financialImpact: 'Increases the operational budget by approximately $60,000/year plus benefits (operational cost).',
          capitalCosts: 'N/A',
          publicBenefit: 'Provides for growth of recreation programs and services.',
          status: 'New (2020)'
        },
        {
          id: '2.F',
          category: 'Consider Reducing the Year-round Availability of Fields and Facilities for Some Activities',
          title: 'In light of continued demand for more space, consider limiting field and facility usage to the traditional “in season” for some activities.',
          timeline: 'Ongoing',
          timelineDetail: '',
          responsibility: 'BREC and Division of Parks and Open Space, in coordination with others.',
          financialImpact: 'Could potentially reduce participation and therefore revenues for in-house programming, and could push increased costs to partner organizations that need to find field space elsewhere.',
          capitalCosts: 'N/A',
          publicBenefit: 'Provides improved athletic field playability and usability.',
          status: 'New (2020)'
        }
      ]
    },
    {
      id: 'must-be-able-to-get-there',
      number: 4,
      name: 'Brookline Residents Must Be Able to Get There',
      description: 'ADA accessibility, inclusion, and improved transportation for students and residents to parks and fields were identified as high needs during public engagement and the survey.',
      items: [
        {
          id: '4.B',
          category: 'Improve Transportation to Parks and Fields',
          title: 'Consider recommending that the High School add transportation services for students to increase access to fields. Work with schools to develop a High School Athletics Transportation Plan to get students to fields across town, and consider a larger initiative involving multiple departments (such as DPW) to develop an improved public transit plan inclusive to all residents for better access to parks and other Town services.',
          timeline: 'Ongoing',
          timelineDetail: '',
          responsibility: 'Division of Parks and Open Space and BREC, and Schools.',
          financialImpact: 'Operational costs for providing transportation.',
          capitalCosts: 'N/A',
          publicBenefit: 'Ensures that students can access fields and parks in a timely manner for practices and contests.',
          status: 'New (2020)'
        }
      ]
    },
    {
      id: 'can-do-it-better',
      number: 5,
      name: 'Brookline Can Do It Better',
      description: 'Although Brookline operates relatively efficiently and public opinion of existing programs and maintenance is high, there is room for improvement in equipment, staff development, and management.',
      items: [
        {
          id: '5.F',
          category: 'Park Maintenance and Management',
          title: 'Continue to provide funding for appropriate equipment requested and justified by staff. The park maintenance staff should submit an annual request for needed equipment along with justification and anticipated costs.',
          timeline: 'Ongoing',
          timelineDetail: '',
          responsibility: 'Division of Parks and Open Space.',
          financialImpact: 'Capital and operational expenditures (funding source exists in the annual operating budget).',
          capitalCosts: '$400,000 per year.',
          publicBenefit: 'Provides equipment needed to adequately maintain the park and open space system.',
          status: 'Revised (2020)'
        },
        {
          id: '5.F',
          category: 'Park Maintenance and Management',
          title: 'Support professional development for the park’s maintenance staff. The park maintenance staff should submit an annual request for needed certifications and trainings, along with justification and anticipated costs.',
          timeline: 'Ongoing',
          timelineDetail: '',
          responsibility: 'Division of Parks and Open Space.',
          financialImpact: 'Operational costs for staff time and/or training costs ($500/person for 15 staff).',
          capitalCosts: 'Suggested budget of $15,000/year for training and professional development.',
          publicBenefit: 'Provides additional tools to accomplish goals. Increases efficiency and job satisfaction for the workforce.',
          status: 'Remaining from 2006'
        }
      ]
    }
  ]
};
